import { useMemo } from 'react';
import { InfiniteScrollList } from '@/components/InfiniteScrollList';
import { ValueRow } from './ValueRow';
import type { LedgerValue } from '@/api/types';

interface ValuesListProps {
    /** Array of ledger values to display. */
    values: LedgerValue[];
    /** The ledger ID these values belong to. */
    ledgerId: number;
    /** ID of the value currently being edited, or null if none. */
    editingValueId: number | null;
    /** Callback when a row's edit button is clicked. */
    onEdit: (valueId: number) => void;
    /** Callback when editing is cancelled. */
    onCancelEdit: () => void;
    /** Callback when save is complete. */
    onSaveComplete: () => void;
    /** Whether there are more pages to load. */
    hasNextPage: boolean;
    /** Whether the next page is currently being fetched. */
    isFetchingNextPage: boolean;
    /** Callback to load the next page. */
    onLoadMore: () => void;
    /** Whether deletion is allowed (false if this is the last value). */
    canDelete: boolean;
    /** Label for the entity type (e.g., "value" or "balance"). */
    entityLabel?: string;
}

interface DateGroup {
    dateKey: string;
    formattedDate: string;
    items: LedgerValue[];
}

/**
 * Groups values by date and returns an array of groups.
 * Each group has a dateKey, formatted date string, and array of items.
 */
function groupValuesByDate(values: LedgerValue[]): DateGroup[] {
    const groups: Map<string, DateGroup> = new Map();

    for (const value of values) {
        // Use dateFormatted (Y-m-d) as the key for grouping
        const dateKey = value.dateFormatted;
        const date = new Date(value.date);
        const formattedDate = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });

        const existing = groups.get(dateKey);
        if (existing) {
            existing.items.push(value);
        } else {
            groups.set(dateKey, {
                dateKey,
                formattedDate,
                items: [value],
            });
        }
    }

    return Array.from(groups.values());
}

/**
 * List of ledger values with infinite scroll support.
 * Uses InfiniteScrollList wrapper for pagination.
 * Groups values by date, showing date headers for groups with multiple entries.
 */
export function ValuesList({
    values,
    ledgerId,
    editingValueId,
    onEdit,
    onCancelEdit,
    onSaveComplete,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
    canDelete,
    entityLabel = 'value',
}: ValuesListProps) {
    const dateGroups = useMemo(() => groupValuesByDate(values), [values]);

    // Track global index to determine isFirst
    let globalIndex = 0;

    return (
        <InfiniteScrollList
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={onLoadMore}
            loadingText={`Loading more ${entityLabel}s...`}
            className="space-y-2"
        >
            {dateGroups.map((group) => (
                <div
                    key={group.dateKey}
                    className={
                        group.items.length > 1
                            ? 'border-l-2 border-accent dark:border-primary-light pl-2'
                            : ''
                    }
                >
                    <div className="space-y-2">
                        {group.items.map((value) => {
                            const isFirst = globalIndex === 0;
                            globalIndex++;
                            return (
                                <ValueRow
                                    key={value.id}
                                    value={value}
                                    ledgerId={ledgerId}
                                    isEditing={editingValueId === value.id}
                                    onEdit={() => onEdit(value.id)}
                                    onCancelEdit={onCancelEdit}
                                    onSaveComplete={onSaveComplete}
                                    canDelete={canDelete || values.length > 1}
                                    isFirst={isFirst}
                                    entityLabel={entityLabel}
                                />
                            );
                        })}
                    </div>
                </div>
            ))}
        </InfiniteScrollList>
    );
}
